import React, { memo, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { checkIsLoadingRefVisible } from '@bcraft-frontend-lib/core'
import { GetGameInfoDGA, HandleAddSlotFrame, PragmaticDGA, Slot, SlotsQueryFilters } from '@bcraft-frontend-lib/slots'
import block from 'bem-cn'
import { AnimatePresence, motion } from 'framer-motion'

import useInfiniteVerticalScroll from 'shared/hooks/useInfiniteVerticalScroll'

import { selectLocaleDict } from 'features/locale/selectors'

import { EmptyFavorites } from '../EmptyFavorites/EmptyFavorites'
import { EmptyFilterResult } from '../EmptyFilterResult/EmptyFilterResult'
import { EmptySearchResult } from '../EmptySearchResult/EmptySearchResult'
import SlotsIcon from '../SlotsIcon/SlotsIcon'

import './AllSlotsList.scss'

const b = block('all-slots-list')

interface IAllSlotsListProps {
  slots?: Slot[]
  filters: SlotsQueryFilters
  modal?: boolean
  addFrame?: HandleAddSlotFrame
  getGameInfo?: GetGameInfoDGA
}

export const AllSlotsList = memo(({ slots = [], filters, modal, addFrame, getGameInfo }: IAllSlotsListProps) => {
  const locale = useSelector(selectLocaleDict, shallowEqual).slots
  const slotsPerRender = modal ? 12 : 28

  const { selectedCategories, selectedProviders, searchString } = filters

  const { current, ref } = useInfiniteVerticalScroll<Slot>({
    data: slots,
    loadingCount: slotsPerRender,
  })

  const allSlots = useMemo(() => {
    if (searchString.length && !current.length) return <EmptySearchResult />
    if (selectedCategories.indexOf('favorite') !== -1 && !slots.length) return <EmptyFavorites />
    if (!slots.length) return <EmptyFilterResult />
    return (
      current?.map((slot, id) => {
        const handleAddFrame = modal ? addFrame : undefined

        const wide = modal && (id % 9 === 0 || id % 14 === 0)
        const slotId = slot.id

        const gameInfo = getGameInfo?.(slot)
        if (gameInfo) {
          return (
            <SlotsIcon
              key={slotId}
              slot={slot}
              addFrame={handleAddFrame}
              wide={wide}
              Image={<PragmaticDGA gameInfo={gameInfo} />}
            />
          )
        }

        return (
          <SlotsIcon
            key={slotId}
            slot={slot}
            addFrame={handleAddFrame}
            wide={wide}
          />
        )
      }) ?? []
    )
  }, [searchString, current, selectedCategories, slots, modal, addFrame, getGameInfo])

  const loading = useMemo(
    () => (
      <>
        {checkIsLoadingRefVisible(current.length, slots.length, slotsPerRender) && (
          <div ref={ref} className={b('loading')}>
            <span>{locale.loading}</span>
          </div>
        )}
      </>
    ),
    [current, slots, locale, ref, slotsPerRender],
  )

  return (
    <AnimatePresence exitBeforeEnter>
      <motion.div
        key={`${selectedCategories.join('-')}-${selectedProviders.join('-')}`}
        data-tip={`${selectedCategories.join('-') || ''}-${selectedProviders.join('-') || ''}`}
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.1 }}>
        <div
          className={b('slots', {
            isEmpty:
              !slots.length ||
              (selectedCategories.indexOf('favorite') !== -1 && !slots.length) ||
              (searchString.length && !current.length),
          })}>
          {allSlots}
        </div>
        {loading}
      </motion.div>
    </AnimatePresence>
  )
})
